import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
  CreditCard,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import api from '@/lib/api';
import Card from '@/components/ui/Card';

/**
 * In-CMS guided setup for subscription billing. Reflects live status (Stripe
 * connected? webhook secret set? plans synced?) and inlines the Stripe-side
 * instructions an operator can't automate (the webhook URL + the exact event
 * set). Backs onto the existing /admin/payments/info + /admin/credentials
 * endpoints; sync status is derived from the plans already loaded by the page.
 */

interface Credential {
  key: string;
  category: string;
  is_set: boolean;
}

interface PaymentsInfo {
  webhook_url: string;
  stripe_mode: 'test' | 'live' | 'unset';
}

interface PlanLike {
  price: number | string;
  stripe_price_id: string | null;
}

// The events the platform webhook actually handles for plan subscriptions.
const REQUIRED_EVENTS = [
  'customer.subscription.created',
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'invoice.paid',
];

function StepRow({
  done,
  title,
  children,
}: {
  done: boolean;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 py-3">
      {done ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
      ) : (
        <Circle className="mt-0.5 h-5 w-5 shrink-0 text-stone-300" />
      )}
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${done ? 'text-stone-500 line-through' : 'text-stone-900'}`}>
          {title}
        </div>
        {!done && children && <div className="mt-1.5 text-sm text-stone-600">{children}</div>}
      </div>
    </div>
  );
}

export default function SubscriptionBillingChecklist({ plans }: { plans: PlanLike[] }) {
  const [info, setInfo] = useState<PaymentsInfo | null>(null);
  const [creds, setCreds] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [infoRes, credRes] = await Promise.all([
          api.get<PaymentsInfo>('/admin/payments/info'),
          api.get<{ credentials: Credential[] }>('/admin/credentials'),
        ]);
        setInfo(infoRes);
        setCreds(credRes.credentials.filter((c) => c.category === 'payments'));
      } catch {
        // Soft-fail: the checklist is guidance, not a blocker.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;

  const keySet = creds.find((c) => c.key === 'STRIPE_SECRET_KEY')?.is_set ?? false;
  const webhookSet = creds.find((c) => c.key === 'STRIPE_WEBHOOK_SECRET')?.is_set ?? false;
  const paidPlans = plans.filter((p) => Number(p.price) > 0);
  const syncedPaid = paidPlans.filter((p) => p.stripe_price_id);
  const plansSynced = paidPlans.length > 0 && syncedPaid.length === paidPlans.length;

  const allDone = keySet && webhookSet && plansSynced;

  function copyWebhook() {
    if (!info?.webhook_url) return;
    navigator.clipboard.writeText(info.webhook_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (allDone) {
    return (
      <Card padding="md" className="border-green-200 bg-green-50">
        <div className="flex items-center gap-2 text-sm text-green-800">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <span className="font-medium">Subscription billing is live</span>
          <span className="text-green-700">
            ({info?.stripe_mode === 'live' ? 'live mode' : 'test mode'}) — customers can subscribe from
            their account Billing page.
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" className="border-amber-200 bg-amber-50/40">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        <CreditCard className="h-5 w-5 text-amber-600" />
        <span className="font-semibold text-stone-900">Set up subscription billing</span>
        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          {[keySet, webhookSet, plansSynced].filter(Boolean).length}/3 done
        </span>
        {collapsed ? (
          <ChevronRight className="ml-auto h-4 w-4 text-stone-400" />
        ) : (
          <ChevronDown className="ml-auto h-4 w-4 text-stone-400" />
        )}
      </button>

      {!collapsed && (
        <div className="mt-2 divide-y divide-amber-100/70">
          <StepRow done={keySet} title="1. Connect your Stripe account">
            Add your Stripe secret key (use a <code>sk_test_…</code> key to trial it).{' '}
            <Link to="/admin/settings/payments" className="font-medium text-amber-700 underline">
              Open Payments settings
            </Link>
            . Don't have a Stripe account yet?{' '}
            <a
              href="https://dashboard.stripe.com/register"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-amber-700 underline"
            >
              Create one <ExternalLink className="inline h-3 w-3" />
            </a>{' '}
            then grab the key from <strong>Developers → API keys</strong>.
          </StepRow>

          <StepRow done={webhookSet} title="2. Add the Stripe webhook">
            In Stripe → <strong>Developers → Webhooks → Add endpoint</strong>, paste this URL:
            <div className="mt-1.5 flex items-center gap-2 rounded-md border border-stone-200 bg-white p-2">
              <code className="min-w-0 flex-1 truncate text-xs text-stone-700">
                {info?.webhook_url ?? 'https://api.<your-domain>/webhooks/stripe'}
              </code>
              <button
                onClick={copyWebhook}
                className="inline-flex items-center gap-1 rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-50"
              >
                <Copy className="h-3 w-3" />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="mt-1.5">
              Subscribe to these events, then paste the signing secret (<code>whsec_…</code>) into
              Payments settings:
              <ul className="mt-1 space-y-0.5">
                {REQUIRED_EVENTS.map((e) => (
                  <li key={e}>
                    <code className="text-xs">{e}</code>
                  </li>
                ))}
              </ul>
            </div>
          </StepRow>

          <StepRow
            done={plansSynced}
            title="3. Sync your plans to Stripe"
          >
            {paidPlans.length === 0
              ? 'Create at least one paid plan below, then save it — saving auto-creates the Stripe product + price.'
              : `Open and Save each paid plan below to create its Stripe price (${syncedPaid.length}/${paidPlans.length} synced). Free plans don't need syncing.`}
          </StepRow>

          <StepRow done={false} title="4. Test a checkout">
            As a non-complimentary customer, go to <code>/account/billing</code>, subscribe, and pay
            with Stripe's test card <code>4242 4242 4242 4242</code> (any future expiry / CVC / ZIP).
          </StepRow>
        </div>
      )}
    </Card>
  );
}
